import { useState, useRef, useCallback, useEffect, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Play, Square, Send, Loader2, SkipForward, RotateCcw, AlertCircle } from 'lucide-react';
import { MAX_EDIT_BUDGET, type BuildPlan } from '../../types';
import { isBuildPlanFullyVerified } from '../../lib/build-plan-progress';
import {
  BILLING_STOP_FAILURE_CODES,
  STEP_TOO_LARGE_FAILURE_CODES,
} from '../../lib/builder-failure-codes';

interface StepExecutionPanelProps {
  buildPlan: BuildPlan;
  autoApprove: boolean;
  onAutoApproveChange: (value: boolean) => void;
  onStopGeneration: () => void;
  onSendFeedback: (message: string) => void;
  /** Advances the build pointer. Resolves true on success, false if the backend
   * rejected the advance (e.g. 409 STEP_NOT_BUILT) so callers can skip the
   * follow-up "continue" prompt. */
  onAdvanceStep: () => Promise<boolean>;
  isProcessing: boolean;
  /**
   * Typed failure of the last build attempt for the CURRENT step (stability
   * overhaul Phase 4). Drives the failure card: human reason + Retry +
   * Simplify instead of a bare Continue.
   */
  stepFailure?: { code: string; message: string } | null;
  /** Re-attempt the failed step (resume from files already written). */
  onRetryStep?: () => void;
  /** Ask the model to split the failing step into smaller steps. */
  onSimplifyStep?: () => void;
}

export function StepExecutionPanel({
  buildPlan,
  autoApprove,
  onAutoApproveChange,
  onStopGeneration,
  onSendFeedback,
  onAdvanceStep,
  isProcessing,
  stepFailure,
  onRetryStep,
  onSimplifyStep,
}: StepExecutionPanelProps) {
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasGraph = !!buildPlan.graph;
  const currentIndex = buildPlan.currentStepIndex;
  const totalSteps = buildPlan.steps.length;
  const currentStep =
    currentIndex >= 0 && currentIndex < totalSteps ? buildPlan.steps[currentIndex] : null;

  // Graph-aware: use graph node count for progress
  const graphVerifiedCount = hasGraph
    ? buildPlan.graph!.nodes.filter((n) => n.status === 'verified').length
    : 0;
  const graphTotalNodes = hasGraph ? buildPlan.graph!.nodes.length : 0;
  const currentGraphNode =
    hasGraph && buildPlan.currentNodeId
      ? buildPlan.graph!.nodes.find((n) => n.id === buildPlan.currentNodeId)
      : null;

  // Steps-only plans are done only once the index has also run off the end.
  const allStepsVerified =
    isBuildPlanFullyVerified(buildPlan) && (hasGraph || currentIndex >= totalSteps);

  // Edit budget is exhausted once editCount reaches the backend's effective
  // allowance: max(MAX_EDIT_BUDGET, editBudget) — editBudget is the monotonic
  // ceiling the backend raises when the user sends a message on an exhausted
  // step. The backend caps editCount at the allowance and refuses the NEXT
  // edit once it's reached, so the panel must flag "needs your guidance" at
  // >= (not >) — a strict > never fired, because the backend never lets the
  // count climb past the cap. Values mirror @fias/builder-types — keep in sync
  // (a prior drift hid the Continue / Next Step buttons before the backend
  // blocked when the cap went 12 → 30).
  const editAllowance = Math.max(
    MAX_EDIT_BUDGET,
    currentStep?.editBudget ?? 0,
    (hasGraph ? currentGraphNode?.editBudget : 0) ?? 0,
  );
  // Effective count is the max of the step and its graph-node mirror — the two
  // can diverge under merge-lag, and the backend blocks on the max.
  const editCount = Math.max(
    currentStep?.editCount ?? 0,
    (hasGraph ? currentGraphNode?.editCount : 0) ?? 0,
  );
  const isBudgetBlocked = currentStep != null && editCount >= editAllowance;

  // Authoritative status of the current node: the graph node in graph mode (the
  // pointer node can be 'building' in the graph while its steps[] mirror lags at
  // 'pending'), otherwise the step. All review/continue logic keys off this so
  // the two representations can't disagree.
  const currentStatus = (hasGraph ? currentGraphNode?.status : currentStep?.status) ?? null;

  // Step review mode: AI idle, auto off, work remains, and the current step is
  // genuinely built (verified) — i.e. ready to review and advance.
  const isInReview =
    !isProcessing && !autoApprove && !allStepsVerified && currentStatus === 'verified';

  // Find next step title for the "Next Step" button. Scan steps[] (topological
  // build order) rather than graph.nodes (AI-authored array order) so the label
  // names the step the server will actually advance to on a branching plan.
  const nextStepTitle = (() => {
    if (!hasGraph || !buildPlan.graph) return null;
    const nextStep = buildPlan.steps.find(
      (s) => s.status !== 'verified' && s.id !== currentStep?.id,
    );
    return nextStep?.title ?? null;
  })();

  // Auto-show feedback input when budget is blocked and AI is not processing
  // (only in auto mode — in step-by-step mode the main prompt input handles this)
  useEffect(() => {
    if (autoApprove && isBudgetBlocked && !isProcessing && !showFeedbackInput) {
      setShowFeedbackInput(true);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [autoApprove, isBudgetBlocked, isProcessing, showFeedbackInput]);

  // Hide the inline feedback textarea when auto-approve is turned off,
  // since the main prompt input handles chat in step-by-step mode.
  useEffect(() => {
    if (!autoApprove && showFeedbackInput) {
      setShowFeedbackInput(false);
    }
  }, [autoApprove, showFeedbackInput]);

  const handleStop = useCallback(() => {
    onAutoApproveChange(false);
    onStopGeneration();
  }, [onStopGeneration, onAutoApproveChange]);

  const handleSendFeedback = useCallback(() => {
    const trimmed = feedbackText.trim();
    if (!trimmed) return;
    onSendFeedback(trimmed);
    setFeedbackText('');
    setShowFeedbackInput(false);
  }, [feedbackText, onSendFeedback]);

  const handleContinue = useCallback(() => {
    onSendFeedback(
      'Continue. If the current step is fully built, verify and advance to the next step.',
    );
  }, [onSendFeedback]);

  const handleNextStep = useCallback(async () => {
    // Mechanically advance the step pointer via the backend endpoint, then tell
    // the AI to build the next step — but ONLY if the advance actually succeeded.
    // A rejected advance (409: current step not built, or a concurrent write
    // raced) resolves to false and reconciles server state, so we must not tell
    // the AI to continue past a step the backend refused to advance.
    const advanced = await onAdvanceStep();
    if (advanced) onSendFeedback('Continue to the next step.');
  }, [onAdvanceStep, onSendFeedback]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendFeedback();
      }
    },
    [handleSendFeedback],
  );

  const hasUnfinishedWork = hasGraph
    ? buildPlan.graph!.nodes.some((n) => n.status !== 'verified')
    : buildPlan.steps.some((s) => s.status !== 'verified');

  // When auto-approve is toggled ON and there's unfinished work, resume building.
  const prevAutoApproveRef = useRef(autoApprove);
  useEffect(() => {
    const wasOff = !prevAutoApproveRef.current;
    prevAutoApproveRef.current = autoApprove;
    if (autoApprove && wasOff && !isProcessing && hasUnfinishedWork && !allStepsVerified) {
      handleContinue();
    }
  }, [autoApprove, isProcessing, hasUnfinishedWork, allStepsVerified, handleContinue]);

  // Show Continue when the AI is idle and the current step is UNBUILT (pending or
  // mid-build), or there's no current step yet but work remains (plan just
  // approved). This is the only forward action on an unbuilt step — guided mode
  // never skips one. Hidden in review mode (the step is verified → Next Step).
  // Failure card (Phase 4): the last attempt for the current step failed with
  // a typed reason. Retry/Simplify replace the bare Continue — the step
  // remains re-buildable server-side ('building' at the pointer is the normal
  // in-progress state); what was missing was an honest reason + affordance.
  const showFailure =
    !isProcessing &&
    !showFeedbackInput &&
    !isBudgetBlocked &&
    !allStepsVerified &&
    stepFailure != null &&
    (currentStatus === 'building' || currentStatus === 'pending');

  // Which remedies fit the failure. Retry always (a billing stop is retried
  // after adding credits); Simplify only when time ran out building the step.
  const failureCode = stepFailure?.code ?? '';
  const showSimplify = showFailure && STEP_TOO_LARGE_FAILURE_CODES.has(failureCode);
  const failureHeadline = BILLING_STOP_FAILURE_CODES.has(failureCode)
    ? 'stopped because of credits'
    : 'last attempt failed';

  const showContinue =
    !isProcessing &&
    !showFeedbackInput &&
    !isBudgetBlocked &&
    !allStepsVerified &&
    !isInReview &&
    !showFailure &&
    (currentStatus === 'building' ||
      currentStatus === 'pending' ||
      (!currentStatus && hasUnfinishedWork));

  // "Next Step" advances FROM a finished step, so it only appears in review mode
  // (which already requires the current step to be verified). An unbuilt step
  // shows Continue, not Next — guided mode never skips an unbuilt step.
  const showNextStep = isInReview && !showFeedbackInput && !isBudgetBlocked && hasUnfinishedWork;

  return (
    <div className="border-t bg-muted/20">
      <div className="flex items-center gap-3 px-4 py-2 min-h-[48px]">
        {/* Step info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium flex items-center gap-1.5">
            {isBudgetBlocked ? (
              `Step ${currentIndex + 1}: ${currentStep!.title} — needs your guidance`
            ) : allStepsVerified ? (
              isProcessing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" /> Finalizing build...
                </>
              ) : (
                'Build complete'
              )
            ) : isInReview ? (
              (() => {
                const justVerifiedNode =
                  hasGraph && buildPlan.currentNodeId
                    ? buildPlan.graph!.nodes.find((n) => n.id === buildPlan.currentNodeId)
                    : null;
                const justVerifiedIndex =
                  hasGraph && justVerifiedNode
                    ? buildPlan.graph!.nodes.indexOf(justVerifiedNode) + 1
                    : currentIndex + 1;
                const total = hasGraph ? graphTotalNodes : totalSteps;
                return `Step ${justVerifiedIndex} of ${total} complete — review the result`;
              })()
            ) : showFailure && (hasGraph ? currentGraphNode : currentStep) ? (
              <>
                <AlertCircle className="h-3.5 w-3.5 text-destructive flex-shrink-0" />
                {hasGraph && currentGraphNode
                  ? `Node ${graphVerifiedCount + 1} of ${graphTotalNodes}: ${currentGraphNode.title} — ${failureHeadline}`
                  : `Step ${currentIndex + 1} of ${totalSteps}: ${currentStep?.title} — ${failureHeadline}`}
              </>
            ) : hasGraph && currentGraphNode ? (
              `Node ${graphVerifiedCount + 1} of ${graphTotalNodes}: ${currentGraphNode.title}`
            ) : currentStep ? (
              `Step ${currentIndex + 1} of ${totalSteps}: ${currentStep.title}`
            ) : !currentStep && hasUnfinishedWork ? (
              isProcessing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" /> Fias AI is
                  thinking...
                </>
              ) : (
                'Ready to build — press Continue to start'
              )
            ) : hasGraph ? (
              `${graphTotalNodes} nodes complete`
            ) : (
              `${totalSteps} steps complete`
            )}
          </p>
        </div>

        {/* Stop button (shown when AI is streaming) */}
        {isProcessing && (
          <Button
            variant="destructive"
            size="icon"
            className="h-7 w-7"
            onClick={handleStop}
            aria-label="Stop generation"
          >
            <Square className="h-3.5 w-3.5" />
          </Button>
        )}

        {/* Step/node progress dots */}
        <div className="flex items-center gap-1">
          {(hasGraph ? buildPlan.graph!.nodes : buildPlan.steps).map(
            (item: { id: string; title: string; status: string }) => (
              <div
                key={item.id}
                className={`h-2 w-2 rounded-full transition-colors ${
                  item.status === 'verified'
                    ? 'bg-primary'
                    : item.status === 'building'
                      ? showFailure &&
                        (hasGraph
                          ? item.id === buildPlan.currentNodeId
                          : item.id === currentStep?.id)
                        ? 'bg-destructive/70'
                        : 'ring-2 ring-primary bg-transparent'
                      : 'bg-muted-foreground/30'
                }`}
                title={`${item.title} (${item.status})`}
              />
            ),
          )}
        </div>

        {/* Auto-approve toggle (hidden for single-step plans) */}
        {(hasGraph ? graphTotalNodes : totalSteps) > 1 && (
          <div className="flex items-center gap-1.5">
            <Checkbox
              id="auto-approve"
              checked={autoApprove}
              onCheckedChange={(checked) => onAutoApproveChange(checked === true)}
            />
            <Label htmlFor="auto-approve" className="text-xs cursor-pointer">
              Auto
            </Label>
          </div>
        )}

        {/* Failure affordances (Phase 4): Retry + Simplify replace Continue */}
        {showFailure && onRetryStep && (
          <Button size="sm" onClick={onRetryStep}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
            Retry
          </Button>
        )}
        {showSimplify && onSimplifyStep && (
          <Button size="sm" variant="outline" onClick={onSimplifyStep}>
            Simplify this step
          </Button>
        )}

        {/* Continue button (shown when AI is idle in auto mode and step is unfinished) */}
        {showContinue && (
          <Button size="sm" onClick={handleContinue}>
            <Play className="h-3.5 w-3.5 mr-1" />
            Continue
          </Button>
        )}

        {/* Next Step button (shown in review mode when user is ready to advance) */}
        {showNextStep && (
          <Button size="sm" onClick={handleNextStep}>
            <SkipForward className="h-3.5 w-3.5 mr-1" />
            {nextStepTitle ? `Next: ${nextStepTitle}` : 'Next Step'}
          </Button>
        )}
      </div>

      {/* Status hint */}
      {showContinue && (
        <div className="px-4 py-1.5 border-t border-border/50 bg-muted/30">
          <p className="text-xs text-muted-foreground">
            {!currentStep && hasUnfinishedWork ? (
              'Plan approved. Press Continue to start building.'
            ) : (
              <>
                AI paused between tool calls. Press Continue to keep building
                {' or '}
                <button
                  type="button"
                  className="underline hover:text-foreground"
                  onClick={() => {
                    setShowFeedbackInput(true);
                    setTimeout(() => textareaRef.current?.focus(), 0);
                  }}
                >
                  give feedback
                </button>
                {' to redirect.'}
              </>
            )}
          </p>
        </div>
      )}

      {/* Review mode hint */}
      {showNextStep && (
        <div className="px-4 py-1.5 border-t border-border/50 bg-muted/30">
          <p className="text-xs text-muted-foreground">
            Review the result in the preview. Chat to request changes, or advance to the next step.
          </p>
        </div>
      )}

      {/* Feedback input row (shown after stop) */}
      {showFeedbackInput && !isProcessing && (
        <div className="flex items-end gap-2 px-4 py-2 border-t border-border/50">
          <Textarea
            ref={textareaRef}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isBudgetBlocked
                ? 'The AI needs guidance — sending a message grants it more edits. Describe what to try next...'
                : 'Describe a correction...'
            }
            className="min-h-[40px] max-h-[120px] resize-none text-sm"
            rows={1}
          />
          <Button
            size="icon"
            className="h-9 w-9 flex-shrink-0"
            onClick={handleSendFeedback}
            disabled={!feedbackText.trim()}
            aria-label="Send feedback"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
